import { ThemeType } from '@app/interfaces/interfaces';
import { hexToRGB } from '@app/utils/utils';
import { css } from 'styled-components/macro';
import { BASE_COLORS } from './constants';
import { darkColorsTheme, antDarkColorsTheme } from './dark/darkTheme';
import { lightColorsTheme } from './light/lightTheme';

export const themeObject = {
  light: lightColorsTheme,
  dark: darkColorsTheme,
};

export const antThemeObject = {
  light: {},
  dark: antDarkColorsTheme,
};

const getThemeVariables = (theme: ThemeType) => css`
  color-scheme: ${theme};
  --primary-color: ${themeObject[theme].primary};
  --primary1-color: ${themeObject[theme].primary1};
  --primary-gradient-color: ${themeObject[theme].primaryGradient};
  --info-color: var(--primary-color);
  --secondary-color: ${themeObject[theme].secondary};
  --error-color: ${themeObject[theme].error};
  --warning-color: ${themeObject[theme].warning};
  --success-color: ${themeObject[theme].success};
  --background-color: ${themeObject[theme].background};
  --secondary-background-color: ${themeObject[theme].secondaryBackground};
  --secondary-background-selected-color: ${themeObject[theme].secondaryBackgroundSelected};
  --tertiary-background-color: ${themeObject[theme].tertiaryBackground};
  --login-secondary-background-color: ${themeObject[theme].loginSecondaryBackground};
  --secondary-icon-color: ${themeObject[theme].secondaryIcon};
  --additional-background-color: ${themeObject[theme].additionalBackground};
  --collapse-background-color: ${themeObject[theme].collapseBackground};
  --timeline-background-color: ${themeObject[theme].timelineBackground};
  --spinner-base-color: ${themeObject[theme].spinnerBase};
  --sider-background-color: ${themeObject[theme].siderBackground};
  --shadow-color: ${themeObject[theme].shadow};
  --border-color: ${themeObject[theme].border};
  --scroll-color: ${themeObject[theme].scroll};

  --primary-rgb-color: ${hexToRGB(themeObject[theme].primary)};
  --info-rgb-color: ${hexToRGB(themeObject[theme].primary)};
  --secondary-rgb-color: ${hexToRGB(themeObject[theme].secondary)};
  --error-rgb-color: ${hexToRGB(themeObject[theme].error)};
  --warning-rgb-color: ${hexToRGB(themeObject[theme].warning)};
  --success-rgb-color: ${hexToRGB(themeObject[theme].success)};
  --background-rgb-color: ${hexToRGB(themeObject[theme].background)};

  --text-main-color: ${themeObject[theme].textMain};
  --text-light-color: ${themeObject[theme].textLight};
  --text-superLight-color: ${themeObject[theme].textSuperLight};
  --text-secondary-color: ${themeObject[theme].textSecondary};
  --text-dark-color: ${themeObject[theme].textDark};
  --text-sider-primary-color: ${themeObject[theme].textSiderPrimary};
  --text-sider-secondary-color: ${themeObject[theme].textSiderSecondary};
  --text-transparency-dashboard-color: ${themeObject[theme].textTransparencyDashboard};
  --subtext-color: ${themeObject[theme].subText};
  --text-blueish-color: ${themeObject[theme].textBlueish};

  --dashboard-map-background-color: ${themeObject[theme].dashboardMapBackground};
  --dashboard-map-circle-color: ${themeObject[theme].dashboardMapCircleColor};
  --dashboard-map-control-disabled-background-color: ${themeObject[theme].dashboardMapControlDisabledBackground};

  --main-background-color: ${themeObject[theme].mainBackground};

  --icon-color: ${themeObject[theme].icon};
  --icon-hover-color: ${themeObject[theme].iconHover};
  --box-shadow: ${themeObject[theme].boxShadow};
  --box-shadow-hover: ${themeObject[theme].boxShadowHover};

  --heading-color: ${themeObject[theme].heading};
  --item-hover-bg: ${themeObject[theme].itemHoverBg};
  --background-base-color: ${themeObject[theme].backgroundColorBase};
  --border-base-color: ${themeObject[theme].borderBase};
  --border-light-color: ${themeObject[theme].borderLight};
  --disabled-color: ${themeObject[theme].disable};
  --disabled-bg-color: ${themeObject[theme].disabledBg};
  --disabled-service-bg-color: ${themeObject[theme].disabledServiceBg};
  --disabled-placeholder-color: ${themeObject[theme].disabledPlaceholderColor};
  --layout-body-bg-color: ${themeObject[theme].layoutBodyBg};
  --layout-header-bg-color: ${themeObject[theme].layoutHeaderBg};
  --layout-sider-bg-color: ${themeObject[theme].layoutSiderBg};
  --input-placeholder-color: ${themeObject[theme].inputPlaceholder};
  --avatar-bg: ${themeObject[theme].avatarBg};
  --alert-text-color: ${themeObject[theme].alertTextColor};
  --breadcrumb-color: ${themeObject[theme].breadcrumb};

  --user-stories-background-color: ${themeObject[theme].userStoriesBackground};
  --stats-background-color: ${themeObject[theme].statsBackground};
  --stats-text-color: ${themeObject[theme].statsText};
  --stats-number-color: ${themeObject[theme].statsNumber};
  --stats-shadow-color: ${themeObject[theme].statsShadow};
  --login-section1-background-color: ${themeObject[theme].loginSection1Background};
  --login-footer-background-color: ${themeObject[theme].loginFooterBackground};
  --phase-header-background-color: ${themeObject[theme].phaseHeaderBackground};
  --action-header-background-color: ${themeObject[theme].actionHeaderBackground};
  --metadata-header-background-color: ${themeObject[theme].metadataHeaderBackground};
  --metadata-table-border-color: ${themeObject[theme].metadataTableBorder};
  --modal-header-background-color: ${themeObject[theme].modalHeaderBackground};
  --modal-close-icon-color: ${themeObject[theme].modalCloseIcon};
  --history-icon-color: ${themeObject[theme].historyIconColor};
  --language-background-hover: ${themeObject[theme].languageHoverColor};
  --country-code-background: ${themeObject[theme].countryCodeBackground};
  --finished-journey-background: ${themeObject[theme].finishedJourneyBackground};
  --steps-wait-text-color: ${themeObject[theme].stepsWaitText};

  --light-white-dark-black-color: ${themeObject[theme].lightWhiteDarkBlack};
  --light-black-dark-white-color: ${themeObject[theme].lightBlackDarkWhite};

  --bubble-text-color: ${themeObject[theme].bubbleText};

  --skipped-color: ${themeObject[theme].skippedColor};
  --partner-logos-background: ${themeObject[theme].partnerLogosBackground};
  --partner-logos-hover: ${themeObject[theme].partnerLogosHover};
  --carousel-btn-background: ${themeObject[theme].carouselBtnBackground};

  --green-text: ${themeObject[theme].greenText};
  --new-journey-text: ${themeObject[theme].newJourneyText};
`;

export const lightThemeVariables = css`
  ${getThemeVariables('light')}
`;

export const darkThemeVariables = css`
  ${getThemeVariables('dark')}
  --ant-success-color-deprecated-bg: ${antThemeObject['dark'].successBg} !important;
  --ant-success-color-deprecated-border: ${antThemeObject['dark'].successBorder} !important;
`;

export const commonThemeVariables = css`
  color-scheme: light dark;
  --white: ${BASE_COLORS.white};
  --black: ${BASE_COLORS.black};
  --green: ${BASE_COLORS.green};
  --orange: ${BASE_COLORS.orange};
  --gray: ${BASE_COLORS.gray};
  --lightgrey: ${BASE_COLORS.lightgrey};
  --violet: ${BASE_COLORS.violet};
  --lightgreen: ${BASE_COLORS.lightgreen};
  --pink: ${BASE_COLORS.pink};
  --blue: ${BASE_COLORS.blue};
  --skyblue: ${BASE_COLORS.skyblue};
  --red: ${BASE_COLORS.red};
  --yellow: ${BASE_COLORS.yellow};
  --lightskyblue: ${BASE_COLORS.lightskyblue};
`;

export const antOverrideCssVariables = css`
  --ant-primary-1: var(--primary1-color) !important;
`;
